import { z } from 'zod';
import {
  HoldingUnitContainerKindSchema,
  HoldingUnitIdentityModeSchema,
  HoldingUnitProductKindSchema,
  HoldingUnitTypeSchema,
} from '../holding-units/holding-units.schema.js';
import type {
  HoldingUnitContainerKind,
  HoldingUnitIdentityMode,
  HoldingUnitProductKind,
  HoldingUnitType,
} from '../holding-units/holding-units.dto.js';

export const inboundReceiptSourceTypeValues = ['INTERNAL_SHIPMENT', 'EXTERNAL_MANUAL'] as const;
export type InboundReceiptSourceType = (typeof inboundReceiptSourceTypeValues)[number];
export const InboundReceiptSourceTypeSchema: z.ZodType<InboundReceiptSourceType> = z.enum(
  inboundReceiptSourceTypeValues,
);

export const inboundReceiptModeValues = ['LOT_TOTAL', 'PER_UNIT'] as const;
export type InboundReceiptMode = (typeof inboundReceiptModeValues)[number];
export const InboundReceiptModeSchema: z.ZodType<InboundReceiptMode> =
  z.enum(inboundReceiptModeValues);

export const receivingGranularityValues = ['COMBINED_UNIT', 'SEPARATE_UNITS'] as const;
export type ReceivingGranularity = (typeof receivingGranularityValues)[number];
export const ReceivingGranularitySchema: z.ZodType<ReceivingGranularity> = z.enum(
  receivingGranularityValues,
);

export type InboundReceiptPostingUnitType = HoldingUnitType;
export type InboundReceiptPostingIdentityMode = Extract<
  HoldingUnitIdentityMode,
  'generic' | 'fixed_asset_cycle'
>;
export type InboundReceiptPostingContainerKind = HoldingUnitContainerKind;
export type InboundReceiptPostingProductKind = HoldingUnitProductKind;

export const InboundReceiptPostingUnitTypeSchema: z.ZodType<InboundReceiptPostingUnitType> =
  HoldingUnitTypeSchema;
export const InboundReceiptPostingIdentityModeSchema: z.ZodType<InboundReceiptPostingIdentityMode> =
  HoldingUnitIdentityModeSchema.extract(['generic', 'fixed_asset_cycle']);
export const InboundReceiptPostingContainerKindSchema: z.ZodType<InboundReceiptPostingContainerKind> =
  HoldingUnitContainerKindSchema;
export const InboundReceiptPostingProductKindSchema: z.ZodType<InboundReceiptPostingProductKind> =
  HoldingUnitProductKindSchema;

export const DEFAULT_RECEIVING_GRANULARITY: ReceivingGranularity = 'COMBINED_UNIT';
export const DEFAULT_INBOUND_RECEIPT_UNIT_TYPE: InboundReceiptPostingUnitType = 'product';
export const DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE: InboundReceiptPostingIdentityMode = 'generic';
export const DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND: InboundReceiptPostingContainerKind = 'none';
export const DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND: InboundReceiptPostingProductKind = 'none';

export const inboundReceiptSourceEntryModeValues = [
  'INTERNAL_LINKED',
  'SIMPLE',
  'DETAILED',
] as const;
export type InboundReceiptSourceEntryMode = (typeof inboundReceiptSourceEntryModeValues)[number];
export const InboundReceiptSourceEntryModeSchema: z.ZodType<InboundReceiptSourceEntryMode> = z.enum(
  inboundReceiptSourceEntryModeValues,
);

export const inboundReceiptStatusValues = ['CONFIRMED', 'CANCELED'] as const;
export type InboundReceiptStatus = (typeof inboundReceiptStatusValues)[number];
export const InboundReceiptStatusSchema: z.ZodType<InboundReceiptStatus> = z.enum(
  inboundReceiptStatusValues,
);

export const incomingTransferReceiptStatusValues = ['NONE', 'CONFIRMED'] as const;
export type IncomingTransferReceiptStatus = (typeof incomingTransferReceiptStatusValues)[number];
export const IncomingTransferReceiptStatusSchema: z.ZodType<IncomingTransferReceiptStatus> = z.enum(
  incomingTransferReceiptStatusValues,
);

export const attachmentAvailabilityStatusValues = [
  'PENDING',
  'AVAILABLE',
  'BLOCKED',
  'CONSUMED',
  'CANCELED',
] as const;
export type AttachmentAvailabilityStatus = (typeof attachmentAvailabilityStatusValues)[number];
export const AttachmentAvailabilityStatusSchema: z.ZodType<AttachmentAvailabilityStatus> = z.enum(
  attachmentAvailabilityStatusValues,
);

export const predictedSettlementPredictionStatusValues = [
  'NOT_CALCULATED',
  'CALCULATED',
  'ADVISORY_ONLY',
] as const;
export type PredictedSettlementPredictionStatus =
  (typeof predictedSettlementPredictionStatusValues)[number];
export const PredictedSettlementPredictionStatusSchema: z.ZodType<PredictedSettlementPredictionStatus> =
  z.enum(predictedSettlementPredictionStatusValues);

export const predictedSettlementToleranceStatusValues = [
  'UNKNOWN',
  'WITHIN_2_5',
  'OVER_2_5',
] as const;
export type PredictedSettlementToleranceStatus =
  (typeof predictedSettlementToleranceStatusValues)[number];
export const PredictedSettlementToleranceStatusSchema: z.ZodType<PredictedSettlementToleranceStatus> =
  z.enum(predictedSettlementToleranceStatusValues);

export const predictedSettlementPaymentBasisValues = ['UNKNOWN', 'SHIPPED', 'RECEIVED'] as const;
export type PredictedSettlementPaymentBasis =
  (typeof predictedSettlementPaymentBasisValues)[number];
export const PredictedSettlementPaymentBasisSchema: z.ZodType<PredictedSettlementPaymentBasis> =
  z.enum(predictedSettlementPaymentBasisValues);

export const officialSettlementCaptureSourceValues = ['MANUAL', 'CSV_IMPORT'] as const;
export type OfficialSettlementCaptureSource =
  (typeof officialSettlementCaptureSourceValues)[number];
export const OfficialSettlementCaptureSourceSchema: z.ZodType<OfficialSettlementCaptureSource> =
  z.enum(officialSettlementCaptureSourceValues);

export const officialComparisonStructuralStatusValues = ['PENDING', 'VERIFIED', 'BLOCKED'] as const;
export type OfficialComparisonStructuralStatus =
  (typeof officialComparisonStructuralStatusValues)[number];
export const OfficialComparisonStructuralStatusSchema: z.ZodType<OfficialComparisonStructuralStatus> =
  z.enum(officialComparisonStructuralStatusValues);

export const officialComparisonInputStatusValues = ['NOT_RUN', 'MATCHED', 'DIVERGED'] as const;
export type OfficialComparisonInputStatus = (typeof officialComparisonInputStatusValues)[number];
export const OfficialComparisonInputStatusSchema: z.ZodType<OfficialComparisonInputStatus> = z.enum(
  officialComparisonInputStatusValues,
);

export const officialComparisonOutputStatusValues = ['NOT_RUN', 'MATCHED', 'DIVERGED'] as const;
export type OfficialComparisonOutputStatus = (typeof officialComparisonOutputStatusValues)[number];
export const OfficialComparisonOutputStatusSchema: z.ZodType<OfficialComparisonOutputStatus> =
  z.enum(officialComparisonOutputStatusValues);

export const officialComparisonStatusValues = [
  'NOT_RUN',
  'MATCHED',
  'INPUT_DIVERGENCE',
  'CALC_DIVERGENCE',
  'INPUT_AND_CALC_DIVERGENCE',
] as const;
export type OfficialComparisonStatus = (typeof officialComparisonStatusValues)[number];
export const OfficialComparisonStatusSchema: z.ZodType<OfficialComparisonStatus> = z.enum(
  officialComparisonStatusValues,
);

export const officialComparisonReviewStatusValues = [
  'NOT_REQUIRED',
  'REQUIRED',
  'APPROVED',
] as const;
export type OfficialComparisonReviewStatus = (typeof officialComparisonReviewStatusValues)[number];
export const OfficialComparisonReviewStatusSchema: z.ZodType<OfficialComparisonReviewStatus> =
  z.enum(officialComparisonReviewStatusValues);

export const inventoryPostingStatusValues = ['NOT_POSTED', 'POSTED', 'REVERSED'] as const;
export type InventoryPostingStatus = (typeof inventoryPostingStatusValues)[number];
export const InventoryPostingStatusSchema: z.ZodType<InventoryPostingStatus> = z.enum(
  inventoryPostingStatusValues,
);

export const inboundDraftRevalidationCodeValues = [
  'SOURCE_UNAVAILABLE',
  'SOURCE_CANCELED',
  'SOURCE_NOT_DEPARTED',
  'SOURCE_RECEIVER_MISMATCH',
  'SOURCE_MATERIAL_CHANGED',
  'SOURCE_ALREADY_RECEIVED',
] as const;
export type InboundDraftRevalidationCode = (typeof inboundDraftRevalidationCodeValues)[number];
export const InboundDraftRevalidationCodeSchema: z.ZodType<InboundDraftRevalidationCode> = z.enum(
  inboundDraftRevalidationCodeValues,
);
